import { z } from 'zod';
import { runStatusSchema, validationOutcomeSchema } from '../../common/schemas/enum.schemas.js';
import { idParamSchema, uuidSchema } from '../../common/schemas/id.schemas.js';
import { paginatedSchema, paginationInputSchema, sortInputSchema } from '../../common/schemas/pagination.schemas.js';
/** The two template flavours: a workbook with a reference sheet, or a plain single-table CSV. */
export const importFormatSchema = z.enum(['xlsx', 'csv']);
export const MAX_IMPORT_FILE_BASE64_LENGTH = 12_000_000;
/**
 * Files travel as base64 in a JSON body rather than as multipart or a `Blob` output (see the service's
 * `ImportService` docs): one transport for the template download and both uploads, identical through the
 * browser's `OpenAPILink` and a plain `curl`, and fully describable in zod so the OpenAPI surface stays honest.
 */
export const importFileSchema = z.object({
    filename: z.string().trim().min(1).max(255),
    contentBase64: z.string().min(1).max(MAX_IMPORT_FILE_BASE64_LENGTH),
    mimeType: z.string().trim().min(1).max(128),
});
export const downloadImportTemplateInputSchema = z.object({
    format: importFormatSchema.default('xlsx'),
});
/** What `preview` and `commit` both accept — the server is stateless, so the file is sent twice. */
export const importUploadInputSchema = z.object({
    filename: z.string().trim().min(1).max(255),
    contentBase64: z.string().min(1).max(MAX_IMPORT_FILE_BASE64_LENGTH),
});
/**
 * `ACCEPT` — imports and the validity engine calls it VALID.
 * `WARN` — imports, but the engine's verdict is not clean (VALID_WITH_WARNING, or INVALID on a rule failure).
 * `REJECT` — cannot become a run at all: a bad cell, an unknown code, an incoherent scenario, or a duplicate.
 */
export const importVerdictSchema = z.enum(['ACCEPT', 'WARN', 'REJECT']);
/** A dry-run rule verdict: the run's validity table before any row is written. */
export const importRuleOutcomeSchema = z.object({
    ruleKey: z.string().min(1),
    outcome: validationOutcomeSchema,
    actualValue: z.string().nullable(),
    expected: z.string().nullable(),
    message: z.string().nullable(),
});
export const importComputedResultSchema = z.object({
    value: z.number(),
    unit: z.string().min(1),
});
/** The codes the row named, echoed back so the preview table can show what was resolved. */
export const importResolvedCodesSchema = z.object({
    vehicleCode: z.string().nullable(),
    scenarioCode: z.string().nullable(),
    routeCode: z.string().nullable(),
});
export const importPreviewRowSchema = z.object({
    /** The spreadsheet's own row number (the header is row 1), so a reason points at a findable line. */
    rowNumber: z.number().int().min(1),
    verdict: importVerdictSchema,
    reason: z.string().nullable(),
    ruleOutcomes: z.array(importRuleOutcomeSchema),
    computedResult: importComputedResultSchema.nullable(),
    resolved: importResolvedCodesSchema,
    /** The status the run would carry after submit; `null` for a rejected row, which is never written. */
    wouldBeStatus: runStatusSchema.nullable(),
});
export const importPreviewSummarySchema = z.object({
    total: z.number().int().min(0),
    accept: z.number().int().min(0),
    warn: z.number().int().min(0),
    reject: z.number().int().min(0),
});
export const importPreviewOutputSchema = z.object({
    rows: z.array(importPreviewRowSchema),
    summary: importPreviewSummarySchema,
});
/** A batch row is only written on commit, so it is either the committed history or a failed attempt. */
export const importBatchStatusSchema = z.enum(['COMMITTED', 'FAILED']);
export const importErrorRowSchema = z.object({
    rowNumber: z.number().int().min(1),
    /** A machine-readable reason, e.g. `UNKNOWN_VEHICLE_CODE`; absent on older rows. */
    code: z.string().min(1).optional(),
    reason: z.string().min(1),
});
export const importBatchSchema = z.object({
    id: uuidSchema,
    filename: z.string().min(1),
    uploadedBy: uuidSchema.nullable(),
    uploadedByName: z.string().nullable(),
    rowCount: z.number().int().min(0),
    acceptedCount: z.number().int().min(0),
    rejectedCount: z.number().int().min(0),
    status: importBatchStatusSchema,
    createdAt: z.string().datetime(),
});
export const importBatchDetailSchema = importBatchSchema.extend({
    errorReport: z.array(importErrorRowSchema),
});
export const commitImportOutputSchema = z.object({
    batch: importBatchDetailSchema,
    /** The runs the batch created, in row order — the link target of the success panel. */
    runIds: z.array(uuidSchema),
});
export const IMPORT_BATCH_SORT_FIELDS = ['createdAt', 'filename', 'status'];
export const importBatchSortBySchema = z.enum(IMPORT_BATCH_SORT_FIELDS);
export const listImportBatchesInputSchema = z.object({
    ...paginationInputSchema.shape,
    ...sortInputSchema.shape,
    sortBy: importBatchSortBySchema.default('createdAt'),
    sortDirection: z.enum(['asc', 'desc']).default('desc'),
    status: importBatchStatusSchema.optional(),
});
export const listImportBatchesOutputSchema = paginatedSchema(importBatchSchema);
export const getImportBatchInputSchema = idParamSchema;
//# sourceMappingURL=import.schemas.js.map